// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { personSchema } from '@/account/api/types';
import { transportReferenceDetailsSchema } from '@/constants/shipping-carrier';
import {
  roLineItemStatusSchema,
  roStatusSchema,
} from '@/return-order/constants/status';
import {
  sparseExchangeOrderSchema,
  sparseItemSchema,
  sparseLocationWithIntegrationSchema,
} from '@/sparse/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Schema for return order reason used in filtering and validation.
 */
export const returnOrderReasonSchema = z.enum([
  'defective',
  'damaged',
  'wrong_item',
  'upgrade',
  'end_of_service',
  'customer_cancellation',
  'not_needed',
  'incorrect_spec',
  'provisioning',
  'other',
]);

export type ReturnOrderReason = z.infer<typeof returnOrderReasonSchema>;

/**
 * Single source of truth for reason mapping between frontend and backend.
 */
const REASON_MAP: Record<ReturnOrderReason, string> = {
  defective: 'DEFECTIVE',
  damaged: 'DAMAGED',
  wrong_item: 'WRONG_ITEM',
  upgrade: 'UPGRADE',
  end_of_service: 'END_OF_SERVICE',
  customer_cancellation: 'CUSTOMER_CANCELLATION',
  not_needed: 'NOT_NEEDED',
  incorrect_spec: 'INCORRECT_SPEC',
  provisioning: 'PROVISIONING',
  other: 'OTHER',
};

/**
 * Inverse mapping generated from REASON_MAP.
 */
const INVERSE_REASON_MAP = Object.fromEntries(
  Object.entries(REASON_MAP).map(([k, v]) => [v, k])
) as Record<string, ReturnOrderReason>;

/**
 * Converts frontend reason to API format (uppercase).
 */
export function reasonToAPI(reason: ReturnOrderReason): string {
  return REASON_MAP[reason];
}

/**
 * Converts API reason (uppercase) to frontend format (lowercase).
 * Returns 'other' as fallback for unknown reasons.
 */
export function reasonFromAPI(reason: string): ReturnOrderReason {
  return INVERSE_REASON_MAP[reason] ?? 'other';
}

/**
 * Display labels for return order reasons.
 */
export const RETURN_ORDER_REASON_LABELS: Record<ReturnOrderReason, string> = {
  defective: 'Defective / malfunctioning',
  damaged: 'Damaged in transit or handling',
  wrong_item: 'Incorrect item shipped',
  upgrade: 'Product upgrade or model swap',
  end_of_service: 'End of lease / project / service term',
  customer_cancellation: 'Customer cancellation or offboarding',
  not_needed: 'Surplus / over-stock / no longer needed',
  incorrect_spec: "Incorrect specifications / doesn't meet requirements",
  provisioning: 'Provisioning',
  other: 'Other / administrative return',
};

/**
 * Schema for a return order item.
 * Uses nested item structure where line item binding identifiers (id, key) are at the
 * top level, and catalog item properties are nested under `item`.
 */
const returnOrderItemSchema = z.object({
  // Order line item identifiers (binding id/key)
  id: z.string(),
  key: z.string(),
  // Nested catalog item properties
  item: sparseItemSchema,
  // Quantity fields
  requestedQuantity: z.number(),
  receivedQuantity: z.number(),
  openQuantity: z.number(),
  shippedQuantity: z.number(),
  deliveredQuantity: z.number(),
  status: roLineItemStatusSchema,
});

export type ReturnOrderItem = z.infer<typeof returnOrderItemSchema>;

/**
 * Schema for a return order's receiving by a 3PL provider and its sync state.
 */
const returnOrderThirdPartySchema = z.object({
  isEnabled: z.boolean(),
  // Hardfin's last poll of the 3PL for this return order
  lastSyncedAt: dateTimeSchema.nullable(),
  // The 3PL's last reported data update for this return order
  lastUpdateAt: dateTimeSchema.nullable(),
  // Whether the 3PL binding is currently in an error state
  hasSyncError: z.boolean(),
  syncErrorMessage: z.string().nullable(),
});

/**
 * Schema for a return order.
 * Backend returns 'returnType', but we convert it to reason.
 */
export const returnOrderSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    organizationId: z.string(),
    returnOrderNumber: z.string(),
    returnType: z.string(),
    returnReason: z.string().nullable(),
    exchangeGroup: sparseExchangeOrderSchema.nullable(),
    fromLocation: sparseLocationWithIntegrationSchema.nullable(),
    toLocation: sparseLocationWithIntegrationSchema,
    promiseShipDate: dateSchema.nullable(),
    promiseDeliveryDate: dateSchema.nullable(),
    status: roStatusSchema,
    thirdParty: returnOrderThirdPartySchema,
    orderNotes: z.string().nullable(),
    transportReferenceDetails: transportReferenceDetailsSchema.nullable(),
    items: z.array(returnOrderItemSchema),
    cancelledAt: dateTimeSchema.nullable(),
    cancelledBy: personSchema.nullable(),
    cancellationReason: z.string().nullable(),
    createdAt: dateTimeSchema,
    createdBy: personSchema,
    lastUpdatedAt: dateTimeSchema,
    lastUpdatedBy: personSchema,
    mostRecentReceivedAt: dateTimeSchema.nullable(),
    mostRecentDeliveredAt: dateTimeSchema.nullable(),
    mostRecentShippedAt: dateTimeSchema.nullable(),
  })
  .transform((data) => {
    const { returnType, ...rest } = data;
    return {
      ...rest,
      reason: reasonFromAPI(returnType),
    };
  });

export type ReturnOrder = z.infer<typeof returnOrderSchema>;

/**
 * Schema for creating a return order item.
 */
const createReturnOrderItemSchema = z.object({
  itemId: z.string(),
  requestedQuantity: z.number().min(1),
});

/**
 * Schema for creating a return order.
 * Uses superRefine to validate that returnReason is required when type is 'other'.
 */
export const createReturnOrderSchema = z
  .object({
    returnType: returnOrderReasonSchema,
    returnReason: z.string().nullable(),
    fromLocationId: z.string().nullable(),
    toLocationId: z.string(),
    promiseShipDate: dateSchema.nullable(),
    promiseDeliveryDate: dateSchema.nullable(),
    orderNotes: z.string().nullable(),
    transportReferenceDetails: transportReferenceDetailsSchema.nullable(),
    items: z.array(createReturnOrderItemSchema),
    fileIds: z.array(z.string()).nullable(),
  })
  .superRefine((data, ctx) => {
    if (
      data.returnType === 'other' &&
      (!data.returnReason || data.returnReason.trim() === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Return reason is required when type is "Other"',
        path: ['returnReason'],
      });
    }
  });

export type CreateReturnOrderRequest = z.infer<typeof createReturnOrderSchema>;

/**
 * Schema for updating a return order item.
 */
const updateReturnOrderItemSchema = z.object({
  itemId: z.string(),
  requestedQuantity: z.number().min(1).optional(),
});

/**
 * Schema for updating a return order.
 * Uses superRefine to validate that returnReason is required when type is 'other'.
 */
export const updateReturnOrderSchema = z
  .object({
    returnType: returnOrderReasonSchema.optional(),
    returnReason: z.string().nullable().optional(),
    fromLocationId: z.string().nullable().optional(),
    toLocationId: z.string().optional(),
    promiseShipDate: dateSchema.nullable().optional(),
    promiseDeliveryDate: dateSchema.nullable().optional(),
    orderNotes: z.string().nullable().optional(),
    transportReferenceDetails: transportReferenceDetailsSchema
      .nullable()
      .optional(),
    items: z.array(updateReturnOrderItemSchema).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.returnType === 'other' &&
      data.returnReason !== undefined &&
      (!data.returnReason || data.returnReason.trim() === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Return reason is required when type is "Other"',
        path: ['returnReason'],
      });
    }
  });

export type UpdateReturnOrderRequest = z.infer<typeof updateReturnOrderSchema>;
